// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ItemFilterCallback, ItemSearch } from '@/items/components/item-search';
import type { ItemType } from '@/items/constants/item-types';
import { SparseItem } from '@/sparse/api/types';
import {
  Control,
  FieldValues,
  UseControllerProps,
  useController,
} from 'react-hook-form';

interface Props<Values extends FieldValues> extends UseControllerProps<Values> {
  /**
   * The label attached to the input element.
   */
  label?: string;

  /**
   * A react-hook-form control object used to connect the input element to the
   * form.
   */
  control: Control<Values>;

  /**
   * If provided, this function will be used to filter the item options.
   */
  filter?: ItemFilterCallback;

  /**
   * If provided, these items will be hidden from the dropdown.
   */
  hiddenItems?: SparseItem[];

  /**
   * If provided, the placeholder text will be displayed in the input element.
   */
  placeholderText?: string;

  /**
   * If true, archived items will be shown in the dropdown.
   * Defaults to false.
   */
  includeArchived?: boolean;

  /**
   * If true, only physical items (DEVICE, BULK) will be shown.
   * Non-physical items (SERVICE) will be excluded.
   * Defaults to false.
   */
  physicalOnly?: boolean;

  /**
   * Optional item type filter to show only items of a specific type.
   * If not provided, all item types are shown.
   */
  type?: ItemType;

  /**
   * If provided, sets a minimum width for the dropdown popper.
   */
  popperWidth?: number;

  /**
   * If true, makes the input field take up full width of its container.
   */
  fullWidth?: boolean;

  /**
   * If true, shows a search icon at the start of the input.
   */
  showSearchAdornment?: boolean;
}

/**
 * A controlled item search component that integrates with react-hook-form.
 * Used for selecting items from the unified Item Catalog in forms.
 */
export function FormItemSearch<Values extends FieldValues>(
  props: Props<Values>
) {
  const { field, fieldState } = useController({
    name: props.name,
    control: props.control,
  });

  const value = field.value ?? null;

  return (
    <ItemSearch
      ref={field.ref}
      name={field.name}
      label={props.label}
      value={value}
      filter={props.filter}
      disabled={props.disabled}
      onChange={field.onChange}
      error={Boolean(fieldState.error)}
      helperText={fieldState.error?.message}
      hiddenItems={props.hiddenItems}
      placeholderText={props.placeholderText}
      includeArchived={props.includeArchived}
      physicalOnly={props.physicalOnly}
      type={props.type}
      popperWidth={props.popperWidth}
      fullWidth={props.fullWidth}
      showSearchAdornment={props.showSearchAdornment}
    />
  );
}
